/**
 * Validates an email address for common conventions and provides explanatory messages for validation failures.
 *
 * Validations Covered:
 * - Contains a single '@' symbol separating the local part from the domain.
 * - No consecutive dots in either local part or domain.
 * - No disallowed characters present in the email.
 * - Domain ends with a top-level domain (TLD) separated by a dot, with the TLD having at least two characters.
 *
 * @param email The email address to validate.
 * @returns A tuple with the validation result (true or false) and an explanatory message.
 */

export function isValidCommonEmail(email: string): [boolean, string] {
  const blacklistedChars = [
    "!",
    "#",
    "$",
    "%",
    "&",
    "*",
    "(",
    ")",
    "<",
    ">",
    "[",
    "]",
    "{",
    "}",
    "|",
    "`",
    "^",
    ";",
    ":",
    "~",
    "¤",
    "±",
    "°",
    "µ",
    "¶",
    "·",
    "÷",
    "¬",
    "²",
    "³",
    "¼",
    "½",
    "¾",
    "¦",
    "¨",
    "ª",
    "º",
    "/",
    "\\",
    "=",
    ",",
    "?",
    '"',
    "'",
    "©",
    "®",
    "™",
    "§",
    "£",
    "¥",
    "€",
    "¢",
    "ƒ",
    "¿",
    "¡",
  ];

  const emailSplit = email.split("@");

  const conditions = [
    {
      test: () => emailSplit.length === 1,
      code: "EMAIL_MISSING_AT",
    },
    {
      test: () => emailSplit.length > 2,
      code: "EMAIL_MULTIPLE_AT",
    },
    {
      test: () => email.includes(".."),
      code: "CONSECUTIVE_DOTS",
    },
    {
      test: () => emailSplit[0].length === 0,
      code: "EMAIL_EMPTY_BEFORE_AT",
    },
    {
      test: () => emailSplit[1].length === 0,
      code: "EMAIL_EMPTY_AFTER_AT",
    },
    {
      test: () => blacklistedChars.some(char => email.includes(char)),
      code: "EMAIL_DISALLOWED_CHARS",
    },
    {
      test: () => /\s/g.test(email),
      code: "EMAIL_WHITESPACE_CHARS",
    },
    {
      test: () => {
        const domainParts = emailSplit[1].split(".");
        const domainName = domainParts.slice(0, -1).join(".");
        return !domainName;
      },
      code: "EMAIL_MISSING_DOMAIN",
    },
    {
      // check if domain ends with a top-level domain (TLD)
      test: () => emailSplit[1].split(".").length < 2,
      code: "TLD_MISSING",
    },
    {
      test: () => {
        const tld = emailSplit[1].split(".").slice(-1)[0];
        return tld.length < 2;
      },
      code: "TLD_TOO_SHORT",
    },
  ];

  for (const condition of conditions) {
    if (condition.test()) return [false, condition.code];
  }

  return [true, ""];
}
